import React from "react";
import { Feature, FeatureCollection } from "geojson";
import { useRef, useEffect, useState } from "react";
import mapboxgl, { Map as MapboxMap } from "mapbox-gl";
import MapboxGLWorker from 'mapbox-gl/dist/mapbox-gl-csp-worker?worker';
import "mapbox-gl/dist/mapbox-gl.css";
import { QueryFieldTypes } from "system/query/QueryInfo";
import withQueryWidget, { WithQueryWidgetProps } from "components/widgetParts/queryWidgetComposition/withQueryWidget";
import { getSingleOriginalRequestByName, getSingleWidgetResponseByName } from "system/widget/Widget";


// Assign worker class for Mapbox GL
(mapboxgl as any).workerClass = MapboxGLWorker;

enum MapboxStyle {
  Streets = "mapbox://styles/mapbox/streets-v11",
  Outdoors = "mapbox://styles/mapbox/outdoors-v11",
  Light = "mapbox://styles/mapbox/light-v10",
  Dark = "mapbox://styles/mapbox/dark-v10",
  Satellite = "mapbox://styles/mapbox/satellite-v9",
  SatelliteStreets = "mapbox://styles/mapbox/satellite-streets-v11",
  NavigationDay = "mapbox://styles/mapbox/navigation-day-v1",
  NavigationNight = "mapbox://styles/mapbox/navigation-night-v1"
};

// TODO: This interface should be exported from a more generic ApiResponse place
export interface IRecordsApiResponse {
  cursor: string;
  field_ids: Array<string>;
  field_labels: Array<string>;
  field_types: Array<string>;
  records: Array<Array<any>>;
  records_count: number;
  value_labels: object;
}

function HeatmapMapWidget(props: WithQueryWidgetProps): JSX.Element {
  const mapRef = useRef<null | HTMLDivElement>(null);
  const map = useRef<null | MapboxMap>(null);
  const [longitude, setLongitude] = useState(0);
  const [latitude, setLatitude] = useState(20.0);
  const [zoom, setZoom] = useState(1);

  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'heatmap');
  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'heatmap');
  const responseBody = apiResponseInfo?.responseBody || {};
  const apiResponse = responseBody as IRecordsApiResponse;

  // 1. Setup the Mapbox Map.
  useEffect(() => {
  if (map.current) {
    return;
  }

  map.current = new MapboxMap({
    container: mapRef.current as HTMLDivElement,
    accessToken: import.meta.env.VITE_MAPBOX_ACCESS_TOKEN,
    style: MapboxStyle.Streets,
    center: [longitude, latitude], // starting location: Kansas City
    zoom: zoom,
    maxZoom: 5,
    minZoom: 0,
    preserveDrawingBuffer: true // Required for exporting map as image
  });

  map.current.on("load", () => {
    // Attempt to use the user's selected `latitude_longitude` field (if any).
    const selectedField = props.widget.data?.geohashField || "";
    let fieldIndex = apiResponse.field_ids.indexOf(selectedField);
    if (fieldIndex === -1) {
      // If the user selected field no longer exists, attempt to fall back to the first `latitude_longitude` in the dataset.
      fieldIndex = apiResponse.field_types.indexOf(QueryFieldTypes.latitude_longitude);
    }

    const features: Array<Feature> = [];
    if (fieldIndex >= 0) {
      for (const latlong of apiResponse.records) {
        if (!latlong) {
          continue;
        }
        if (latlong) {
          features.push({
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [latlong[1], latlong[0]]
            },
            properties: {}
          });
        }
      }
    }

    const featureCollection: FeatureCollection = {
      type: "FeatureCollection",
      features
    };

    const sourceName = "source-heatmap";
    map.current?.addSource(sourceName, {
      type: "geojson",
      data: featureCollection
    });

    map.current?.addLayer({
      id: "layer-heatmap",
      type: "heatmap",
      source: sourceName,
      maxzoom: 11,
      paint: {
      // Adjust the heatmap radius by zoom level
      "heatmap-radius": [
        "interpolate",
        ["linear"],
        ["zoom"],
        0, 2,
        11, 20
      ],
      // Phase out the heatmap when the map is zoomed in too granular
      "heatmap-opacity": [
        "interpolate",
        ["linear"],
        ["zoom"],
        9, 1,
        11, 0
      ]
      }
    });
  });

  map.current.on("moveend", () => {
    if (map.current) {
      const lng = parseFloat(map.current.getCenter().lng.toFixed(4));
      const lat = parseFloat(map.current.getCenter().lat.toFixed(4));
      const z = parseFloat(map.current.getZoom().toFixed(2));
      setLongitude(lng);
      setLatitude(lat);
      setZoom(z);
    }

    setLongitude(4);
  });
  }, []);

  // Resize the map when Widget moves or resizes
  map.current?.resize();

  return <div ref={mapRef} style={{ width: "100%", height: "100%" }} />;
}

export default withQueryWidget(HeatmapMapWidget, {
  hasSettingsDialog: true,
  hasQueryDialog: true
});